<template>
  <OperatingcostinvoiceWrapper
    :contractId="selectedContractId"
    :invoiceId="costInvoiceId ?? null"
    :step1-successful="step1Successful"
    :ticket="ticket"
    :isNew="!costInvoiceId"
    :phase="phase"
    :hasAiFeature="hasAiFeature"
    :allowOperatingCostInvoiceLines="allowOperatingCostInvoiceLines"
    :editMode="true"
    :inModal="isInModal"
    :isSubComponent="true"
    :utilityBills="utilityBills"
    :initialDataOverride="{
      ...latestValues,
      pointOfSaleId: fixedPointOfSaleId || undefined,
    }"
    :placeholdersOverride="autoPlaceholders"
    :latestInvoiceIdOverride="latestInvoice?.id ?? null"
    :latestInvoiceLineItemsOverride="latestInvoiceLineItems"
    :disabledFieldsOverride="{
      pointOfSaleId: !!fixedPointOfSaleId,
    }"
    @created="onCreatedWrapper"
    @edited="onEditedWrapper"
    @contractIdChange="onContractSelected"
    @lineItemsCreated="onLineItemsCreated"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import type { OperatingCostInvoiceLineItemSimple } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';
import { useOperatingCostInvoicesConverter } from '@/views/operatingcostinvoices/hooks/useOperatingCostInvoices'; // TODO: "operatingcostinvoice" should have own domain
import type { OperatingCostInvoiceSimple } from '@/views/operatingcostinvoices/operatingCostInvoiceTypes';
import OperatingcostinvoiceWrapper from '@/views/operatingcostinvoices/OperatingcostinvoiceWrapper.vue';
import type { CostInvoicePhase } from '@/views/processes/injectables/uiPlugins/costStatementTypes';
import type { TicketDocumentV1Dto } from '@/openapi';

import type { TicketEnriched } from '../../ticketsTypes';

const { fetchByPosAndConvertToSimple } = useOperatingCostInvoicesConverter();

const props = withDefaults(
  defineProps<{
    utilityBills: TicketDocumentV1Dto[];
    ticket: TicketEnriched;
    costInvoiceId?: string | null;
    step1Successful?: boolean;
    phase?: CostInvoicePhase;
    hasAiFeature?: boolean;
    allowOperatingCostInvoiceLines?: boolean;
    isInModal?: boolean;
  }>(),
  {
    costInvoiceId: null,
    phase: 'both',
    hasAiFeature: true,
  },
);

const emit = defineEmits<{
  (
    e: 'created',
    payload: {
      ok: boolean;
      id?: string;
      sentAt?: string;
      request?: any;
      responseMeta?: any;
    },
  ): void;
  (
    e: 'lineItemsCreated',
    payload: {
      ok: boolean;
    },
  ): void;
}>();

const coststatementinvoices = ref<OperatingCostInvoiceSimple[]>([]);
const selectedContractId = ref<string | undefined>(undefined);

const latestInvoice = ref<OperatingCostInvoiceSimple | null>(null);
const autoPlaceholders = ref<Partial<Record<keyof OperatingCostInvoiceSimple, string>>>({});
const latestValues = ref<Partial<OperatingCostInvoiceSimple>>({});
const latestInvoiceLineItems = ref<OperatingCostInvoiceLineItemSimple[]>([]);

const fixedPointOfSaleId = computed(() => props.ticket.pointOfSale?.id ?? null);

function onCreatedWrapper(payload: { id?: string }) {
  // -> ProcessSlip, Ticket etc. handled in parent component
  emit('created', {
    ok: true,
    id: payload?.id,
    sentAt: new Date().toISOString(),
    request: null,
    responseMeta: {
      status: 'ok',
      source: 'OperatingcostinvoiceWrapper',
    },
  });

  // specific to this component: refresh list
  void fetchData();
}

function onEditedWrapper(_payload: { id?: string }) {
  // refresh only
  void fetchData();
}

function onContractSelected(id: string | null | undefined) {
  selectedContractId.value = id ?? undefined;
}

function onLineItemsCreated(payload: { ok: boolean }) {
  emit('lineItemsCreated', payload);
}

const fetchData = async () => {
  coststatementinvoices.value = await fetchByPosAndConvertToSimple(props.ticket.pointOfSale!.id);
};
</script>
